import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    CheckSquare, Search, X, Plus, FileText, ExternalLink,
    CheckCircle2, XCircle, Pencil, Calendar as CalendarIcon, AlertTriangle,
    LayoutList, Columns3,
} from "lucide-react";

import InstallerShell from "@/components/installer/InstallerShell";
import { PageContainer, PageHeader } from "@/components/layout/PageLayout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
    resolveInitialTasksScope,
    encodeTasksUrl,
    normaliseTaskEngineerParam,
    CALENDAR_UNASSIGNED_SENTINEL,
} from "@/lib/navLinks";
import TaskForm from "@/components/tasks/TaskForm";
import { PriorityBadge, StatusBadge, OverdueBadge, KANBAN_COLUMNS, formatDue } from "@/components/tasks/TaskBadges";
import BulkDeleteModal from "@/components/bulk/BulkDeleteModal";
import { reportBulkDeleteResult } from "@/components/bulk/reportBulkDelete";

const SCOPES = [
    { key: "all",           label: "All tasks" },
    { key: "pending",       label: "Pending" },
    { key: "my",            label: "My tasks" },
    { key: "overdue",       label: "Overdue" },
    { key: "due_today",     label: "Due today" },
    { key: "due_this_week", label: "Due this week" },
    { key: "completed",     label: "Completed" },
    { key: "cancelled",     label: "Cancelled" },
];

const VIEW_KEY = "solarflow_tasks_view";
const SCOPE_KEY = "solarflow_tasks_scope";

export default function InstallerTasks({ embedded = false }) {
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    // VISIT-TASK-001D2 · Role-aware canonical-task navigation.
    // OperationsTasks mounts this page inside OperationsShell — but
    // the previous `/installer/project/:id` navigation is guarded by
    // ``ProtectedRoute role="installer"`` and bounces Ops users to
    // ``/operations``.
    //
    // DEV-020 UAT correction 2 · Ops sessions land on the DEV-020
    // Single Project Focus URL (``/operations/projects/:id?taskId=…``)
    // — never the legacy master-detail selector with ``?project=…``.
    // Installer sessions retain the existing
    // ``/installer/project/:id?taskId=…#phases`` path.
    const roleForRouting = (user?.role || "").toLowerCase();
    const openCanonicalTask = useCallback((t) => {
        if (!t?.project_id) {
            toast.error("This task is missing its project reference and can't be opened.");
            return;
        }
        // The workflow row's ``id`` is the canonical pseudo-id
        // ``canon:<pid>:<real-task-id>``. Peel it off so we can
        // pass a STABLE canonical task id via ``taskId=…`` — the
        // same value the Calendar Required-tasks Open action uses.
        const canonicalTaskId = t.canonical_task_id
            || String(t.id || "").replace(/^canon:[^:]+:/, "")
            || null;
        const qs = new URLSearchParams();
        if (canonicalTaskId) qs.set("taskId", canonicalTaskId);
        if (roleForRouting === "operations") {
            const suffix = qs.toString();
            navigate(
                `/operations/projects/${encodeURIComponent(t.project_id)}${suffix ? `?${suffix}` : ""}`,
            );
            return;
        }
        if (roleForRouting === "sales") {
            // DEV-019 UAT correction 4A · Sales sessions must land
            // on the DEV-020 Sales Focus route (never the Ops or
            // Installer URL). Preserves the canonical taskId
            // querystring so Project Focus can auto-expand the
            // target task, mirroring the Ops branch above.
            const suffix = qs.toString();
            navigate(
                `/sales/projects/${encodeURIComponent(t.project_id)}${suffix ? `?${suffix}` : ""}`,
            );
            return;
        }
        // Installer / Lead / other authorised roles retain the
        // legacy Installer URL (guarded by role="installer" route).
        const installerQs = new URLSearchParams();
        installerQs.set("project", t.project_id);
        if (canonicalTaskId) installerQs.set("taskId", canonicalTaskId);
        navigate(
            `/installer/project/${t.project_id}?${installerQs.toString()}#phases`,
        );
    }, [navigate, roleForRouting]);
    // FIELD-001B1 · The whole tenant-wide Tasks workspace (new task,
    // templates, engineer filter, bulk selection, generic edit modal)
    // is behind `manage_projects`. Ordinary Engineers get a strictly
    // read-only "My tasks" view of what has been assigned to them.
    const canManageProjects = Array.isArray(user?.permissions)
        && user.permissions.includes("manage_projects");
    // FIELD-001C · Project Leads who lead at least one project also
    // need standalone New task. `user.leads_project_ids` is populated
    // by `/auth/me`; empty for non-Leads. Server independently gates
    // `POST /api/tasks` against `projects.lead_engineer_id`.
    //
    // Codex reported the button briefly appearing then disappearing
    // once auth settled on refresh / direct URL loads. Root cause:
    // `/auth/refresh` returns the slim login-user shape that lacks
    // `leads_project_ids`, so before the AuthContext bootstrap
    // enrichment lands (FIELD-001C fix), `user` has no lead ids. We
    // now defer the decision until the AuthContext `loading` flag
    // clears so Project Lead authority is stable after login,
    // refresh and direct URLs and never depends on visiting a
    // project.
    const leadProjectIds = Array.isArray(user?.leads_project_ids) ? user.leads_project_ids : [];
    // DEV-026 · Sales users may create ad-hoc tasks — either standalone
    // personal to-dos (no project) or against Sales-visible projects.
    // Server enforces the actual authorisation on POST /tasks.
    const isSalesRole = (user?.role || "").toLowerCase() === "sales";
    const canCreateTask = !authLoading && (canManageProjects || leadProjectIds.length > 0 || isSalesRole);
    // FIELD-001C · Server-authoritative list of projects the caller
    // may create tasks on. Ops → every project; Field Project Lead →
    // only led projects; ordinary Field User → []. Populated below
    // once auth has settled so the standalone project picker never
    // leaks a project the caller isn't authorised to write against.
    const [taskProjects, setTaskProjects] = useState([]);
    useEffect(() => {
        if (authLoading || !canCreateTask) { setTaskProjects([]); return; }
        let cancelled = false;
        (async () => {
            try {
                const rows = await api.taskCreationOptions();
                if (!cancelled) setTaskProjects(rows || []);
            } catch { if (!cancelled) setTaskProjects([]); }
        })();
        return () => { cancelled = true; };
    }, [authLoading, canCreateTask]);
    // FIELD-001C · URL is the source of truth for engineer/scope/
    // priority/status/project_id. `@/lib/navLinks` centralises the
    // rules (scope defaulting on drill, unassigned sentinel encoding,
    // preservation of unrelated params).
    // VISIT-TASK-001D3 · URL is ALSO the source of truth for
    // `q` (search), `task_category` and the list/kanban view. That
    // way a browser Back after opening a canonical task restores
    // the originating list's search + filters + view exactly.
    const [search, setSearch] = useSearchParams();

    const [view, setView] = useState(() =>
        search.get("view") === "kanban"
            ? "kanban"
            : (localStorage.getItem(VIEW_KEY) || "list"),
    );
    const [scope, setScope] = useState(() =>
        resolveInitialTasksScope(search, localStorage.getItem(SCOPE_KEY)),
    );
    useEffect(() => { localStorage.setItem(VIEW_KEY, view); }, [view]);
    useEffect(() => { localStorage.setItem(SCOPE_KEY, scope); }, [scope]);

    const [filters, setFilters] = useState({
        priority: search.get("priority") || "",
        status: search.get("status") || "",
        engineer_id: normaliseTaskEngineerParam(search.get("engineer_id")),
        task_category: search.get("task_category") || "",
        project_id: search.get("project_id") || "",
        q: search.get("q") || "",
    });

    // State → URL sync. Debounced replace so keystrokes on the
    // search box don't spam history. Selecting All resources or
    // Unassigned mid-session updates the URL so refresh / copied
    // links reproduce the same view.
    useEffect(() => {
        const t = setTimeout(() => {
            const next = encodeTasksUrl(search, {
                scope,
                priority: filters.priority,
                status: filters.status,
                project_id: filters.project_id,
                engineer_id: filters.engineer_id,
                q: filters.q,
                task_category: filters.task_category,
                view,
            });
            const current = search.toString();
            const proposed = next.toString();
            if (current !== proposed) setSearch(next, { replace: true });
        }, 150);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scope, filters.priority, filters.status, filters.project_id, filters.engineer_id, filters.q, filters.task_category, view]);

    const [engineers, setEngineers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [metadata, setMetadata] = useState({ categories: [], priorities: [], statuses: [] });
    const [templates, setTemplates] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selected, setSelected] = useState(new Set());

    const [formOpen, setFormOpen] = useState(false);
    const [formInitial, setFormInitial] = useState(null);
    const [detailTask, setDetailTask] = useState(null);
    const [showTemplateMenu, setShowTemplateMenu] = useState(false);

    // Bootstrap
    // FIELD-001C · Independent per-endpoint fetches. The previous
    // `Promise.all([...])` aborted the whole batch when any single
    // call rejected — most importantly `api.listEngineers` (Ops-only)
    // which throws 403 for Leads / field users. That silently dropped
    // `metadata` too, leaving the shared TaskForm with an empty
    // status / category / priority catalogue and manifesting as
    // "Status offers only In Progress". Each call is now guarded
    // independently so one failure never masks another success.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const settle = (fn) => fn().catch((e) => { console.warn(e); return null; });
            const [engs, projs, md, tmpls] = await Promise.all([
                settle(() => api.listEngineers({ includeArchived: true })),
                settle(() => api.listProjectsSummary()),
                settle(() => api.taskMetadata()),
                settle(() => api.listTaskTemplates()),
            ]);
            if (cancelled) return;
            if (engs) setEngineers(engs);
            if (projs) setProjects(projs.map((p) => ({
                id: p.id, customer_name: p.customer_name,
                address: p.address, system_summary: p.system_summary,
            })));
            if (md) setMetadata(md);
            if (tmpls) setTemplates(tmpls);
        })();
        return () => { cancelled = true; };
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            // Sentinel: engineer_id="__unassigned__" → fetch all and filter client-side
            const isUnassigned = filters.engineer_id === CALENDAR_UNASSIGNED_SENTINEL;
            const apiFilters = isUnassigned ? { ...filters, engineer_id: "" } : filters;
            let data = await api.listTasks({ scope, ...apiFilters });
            if (isUnassigned) {
                data = data.filter((t) => !t.assigned_engineer_id);
            }
            setTasks(data);
            setSelected(new Set());
        } catch (e) {
            setError(formatApiError(e?.response?.data?.detail) || "Failed to load tasks");
        } finally { setLoading(false); }
    }, [scope, filters]);

    useEffect(() => { load(); }, [load]);

    const engMap = useMemo(() => Object.fromEntries(engineers.map((e) => [e.id, e])), [engineers]);
    const activeFilters = Object.values(filters).filter((v) => v !== "").length;

    // Bulk actions ---------------------------------------------------------
    async function bulkAssign(engineerId) {
        try {
            await api.bulkTaskAction({ ids: Array.from(selected), action: "assign", engineer_id: engineerId || null });
            toast.success("Assigned"); await load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Bulk assign failed"); }
    }
    async function bulkPriority(p) {
        try {
            await api.bulkTaskAction({ ids: Array.from(selected), action: "priority", priority: p });
            toast.success("Priority updated"); await load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Bulk failed"); }
    }
    async function bulkComplete() {
        try {
            await api.bulkTaskAction({ ids: Array.from(selected), action: "complete" });
            toast.success("Marked complete"); await load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Bulk failed"); }
    }
    async function bulkCancel() {
        try {
            await api.bulkTaskAction({ ids: Array.from(selected), action: "cancel" });
            toast.success("Cancelled"); await load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Bulk failed"); }
    }

    // Bulk delete (admin-only) --------------------------------------------
    const isAdmin = !!user?.is_security_admin;
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
    async function handleBulkDone(result) {
        reportBulkDeleteResult(result, { labelSingular: "task", labelPlural: "tasks" });
        setSelected(new Set());
        setBulkDeleteOpen(false);
        await load();
    }

    async function applyTemplate(templateId) {
        const projectId = filters.project_id;
        if (!projectId) {
            toast.error("Pick a project first (use the Project filter or open a specific project)");
            setShowTemplateMenu(false);
            return;
        }
        try {
            const r = await api.createFromTemplate({ project_id: projectId, template_id: templateId });
            toast.success(`Template applied — ${r.tasks.length} tasks created`);
            setShowTemplateMenu(false);
            await load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Template failed");
        }
    }

    // Kanban drag/drop -----------------------------------------------------
    const [dragOverCol, setDragOverCol] = useState(null);
    // TASKS-UAT · Kanban columns are ad-hoc Title-Case statuses; the
    // canonical phase-task model only supports three states. This
    // map is the inverse of
    // ``backend/routers/tasks.py::_map_canonical_status`` and matches
    // the transitions Project Focus already exposes for canonical
    // rows. "Waiting" has NO canonical counterpart — dragging a
    // workflow card there is refused UI-side rather than sent as
    // an invalid mutation (see the `null` return path below).
    const KANBAN_TO_CANONICAL_STATUS = {
        "Not Started": "not_started",
        "In Progress": "in_progress",
        "Completed":   "complete",
        // "Waiting" intentionally omitted.
    };
    async function onDropToColumn(status, taskId) {
        setDragOverCol(null);
        const t = tasks.find((x) => x.id === taskId);
        if (!t || t.status === status) return;
        try {
            if (t.kind === "workflow") {
                // TASKS-UAT · Canonical workflow rows must go through
                // the dedicated ``/projects/{pid}/tasks/{tid}``
                // endpoint (same path used by List/detail + Project
                // Focus). Sending the synthetic ``canon:*`` id to
                // ``/tasks/{id}`` returns 404 "Task not found"
                // because ad-hoc storage (`db.tasks`) never contains
                // canonical rows.
                const canonicalStatus = KANBAN_TO_CANONICAL_STATUS[status];
                if (!canonicalStatus) {
                    // "Waiting" (or any future non-canonical column)
                    // has no representation in the canonical model.
                    // Refuse UI-side so the card stays in its
                    // original column and the schema stays clean.
                    toast.error(`Workflow tasks can't be moved to ${status}.`);
                    return;
                }
                if (!t.project_id || !t.canonical_task_id) {
                    // Defence-in-depth: canonical rows always carry
                    // both identifiers (see
                    // ``tasks.py::_canonical_workflow_tasks_for``).
                    // A missing id means the row shape drifted —
                    // refuse rather than issue an invalid PATCH.
                    toast.error("Cannot move this task from Kanban.");
                    return;
                }
                await api.updatePhaseTaskStatus(
                    t.project_id, t.canonical_task_id, canonicalStatus,
                );
            } else {
                await api.updateTask(taskId, { status });
            }
            toast.success("Task moved");
            await load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to move");
        }
    }

    const columns = useMemo(() => {
        const buckets = Object.fromEntries(KANBAN_COLUMNS.map((c) => [c, []]));
        tasks.forEach((t) => {
            if (buckets[t.status]) buckets[t.status].push(t);
        });
        return buckets;
    }, [tasks]);

    // ─────────────────────────────────────────────────────────────────────

    const Shell = embedded ? React.Fragment : InstallerShell;

    return (
        <Shell>
            <PageContainer width="full" testid="tasks-page">
                <PageHeader
                    title="Tasks"
                    icon={CheckSquare}
                    backFallback="/installer"
                    testid="tasks-header"
                    rightSlot={canCreateTask ? (
                        <button
                            onClick={() => { setFormInitial(null); setFormOpen(true); }}
                            data-testid="tasks-new-task"
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#3E9E2E] text-white text-sm font-medium hover:bg-[#348426]"
                        >
                            <Plus size={14} strokeWidth={2} /> New task
                        </button>
                    ) : null}
                />

                {/* View toggle + Template */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <div className="flex items-center gap-1 bg-white border border-[#E5E7EB] rounded-md p-0.5">
                        <button
                            onClick={() => setView("list")}
                            data-testid="tasks-view-list"
                            aria-pressed={view === "list"}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded ${view === "list" ? "bg-[#131B2F] text-white" : "text-[#131B2F] hover:bg-[#F5F5F7]"}`}
                        >
                            <LayoutList size={14} strokeWidth={1.75} /> List
                        </button>
                        <button
                            onClick={() => setView("kanban")}
                            data-testid="tasks-view-kanban"
                            aria-pressed={view === "kanban"}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded ${view === "kanban" ? "bg-[#131B2F] text-white" : "text-[#131B2F] hover:bg-[#F5F5F7]"}`}
                        >
                            <Columns3 size={14} strokeWidth={1.75} /> Kanban
                        </button>
                    </div>

                    <div className="relative">
                        {canManageProjects && (
                        <button
                            onClick={() => setShowTemplateMenu((v) => !v)}
                            data-testid="tasks-template-menu"
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#D1D5DB] bg-white text-sm font-medium hover:bg-[#F5F5F7]"
                        >
                            <FileText size={14} strokeWidth={1.75} /> Template
                        </button>
                        )}
                        {showTemplateMenu && canManageProjects && (
                            <div className="absolute right-0 top-11 z-30 w-72 bg-white border border-[#E5E7EB] rounded-md shadow-lg" data-testid="tasks-template-dropdown">
                                <div className="px-3 py-2 text-xs text-[#4B5565] border-b border-[#F3F4F6]">
                                    {filters.project_id
                                        ? `Applies to selected project`
                                        : `Filter to a project first, then pick a template`}
                                </div>
                                {templates.map((t) => (
                                    <button
                                        key={t.id}
                                        onClick={() => applyTemplate(t.id)}
                                        disabled={!filters.project_id}
                                        data-testid={`tasks-template-${t.id}`}
                                        className="w-full text-left px-3 py-2.5 hover:bg-[#F5F5F7] disabled:opacity-50 disabled:hover:bg-white"
                                    >
                                        <div className="text-sm font-medium text-[#131B2F]">{t.name} · {t.task_count} tasks</div>
                                        <div className="text-xs text-[#4B5565]">{t.description}</div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Scope tabs */}
                <div className="flex flex-wrap gap-1.5 mb-3" data-testid="tasks-scopes" role="tablist">
                    {SCOPES.map((s) => (
                        <button
                            key={s.key}
                            role="tab"
                            aria-selected={scope === s.key}
                            onClick={() => setScope(s.key)}
                            data-testid={`tasks-scope-${s.key}`}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium border ${
                                scope === s.key
                                    ? "bg-[#131B2F] text-white border-[#131B2F]"
                                    : "bg-white text-[#131B2F] border-[#E5E7EB] hover:bg-[#F5F5F7]"
                            }`}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>

                {/* Filter bar */}
                <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="tasks-filters">
                    <div className="flex items-center gap-2 px-3 h-10 rounded-md border border-[#E5E7EB] bg-white flex-1 min-w-[220px]">
                        <Search size={14} strokeWidth={1.75} className="text-[#4B5565]" />
                        <input
                            type="text"
                            value={filters.q}
                            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                            placeholder="Search customer, title, engineer, category…"
                            data-testid="tasks-search"
                            className="flex-1 min-w-0 outline-none text-sm bg-transparent"
                        />
                        {filters.q && (
                            <button aria-label="Clear search" onClick={() => setFilters((f) => ({ ...f, q: "" }))}>
                                <X size={14} strokeWidth={1.75} className="text-[#4B5565]" />
                            </button>
                        )}
                    </div>
                    <select value={filters.priority} onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))} data-testid="tasks-filter-priority" className="h-10 px-2.5 rounded-md border border-[#E5E7EB] bg-white text-sm">
                        <option value="">All priorities</option>
                        {(metadata.priorities || []).map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} data-testid="tasks-filter-status" className="h-10 px-2.5 rounded-md border border-[#E5E7EB] bg-white text-sm">
                        <option value="">Any status</option>
                        {(metadata.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {canManageProjects && (
                    <select value={filters.engineer_id === "__unassigned__" ? "__unassigned__" : filters.engineer_id}
                            onChange={(e) => setFilters((f) => ({ ...f, engineer_id: e.target.value }))}
                            data-testid="tasks-filter-engineer"
                            className="h-10 px-2.5 rounded-md border border-[#E5E7EB] bg-white text-sm">
                        <option value="">All resources</option>
                        <option value="__unassigned__">Unassigned</option>
                        {engineers.filter((e) => e.active !== false).map((e) => <option key={e.id} value={e.id}>{e.displayName}</option>)}
                    </select>
                    )}
                    <select value={filters.task_category} onChange={(e) => setFilters((f) => ({ ...f, task_category: e.target.value }))} data-testid="tasks-filter-category" className="h-10 px-2.5 rounded-md border border-[#E5E7EB] bg-white text-sm">
                        <option value="">All categories</option>
                        {(metadata.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select value={filters.project_id} onChange={(e) => setFilters((f) => ({ ...f, project_id: e.target.value }))} data-testid="tasks-filter-project" className="h-10 px-2.5 rounded-md border border-[#E5E7EB] bg-white text-sm max-w-[220px]">
                        <option value="">All projects</option>
                        {projects.map((p) => <option key={p.id} value={p.id}>{p.customer_name}</option>)}
                    </select>
                    {activeFilters > 0 && (
                        <button
                            onClick={() => setFilters({ priority: "", status: "", engineer_id: "", task_category: "", project_id: "", q: "" })}
                            data-testid="tasks-reset"
                            className="h-10 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm hover:bg-[#F5F5F7]"
                        >
                            Reset ({activeFilters})
                        </button>
                    )}
                </div>

                {/* Bulk actions bar */}
                {selected.size > 0 && (
                    <div className="mb-3 p-2 md:p-3 rounded-lg border border-[#3E9E2E]/30 bg-[#3E9E2E]/10 flex flex-wrap items-center gap-2" data-testid="tasks-bulk-bar">
                        <span className="text-sm font-medium text-[#131B2F]">{selected.size} selected</span>
                        <select onChange={(e) => e.target.value && bulkAssign(e.target.value)} defaultValue="" data-testid="tasks-bulk-assign" className="h-9 px-2 rounded-md border border-[#D1D5DB] bg-white text-sm">
                            <option value="">Assign engineer…</option>
                            {engineers.filter((e) => e.active !== false).map((e) => <option key={e.id} value={e.id}>{e.displayName}</option>)}
                        </select>
                        <select onChange={(e) => e.target.value && bulkPriority(e.target.value)} defaultValue="" data-testid="tasks-bulk-priority" className="h-9 px-2 rounded-md border border-[#D1D5DB] bg-white text-sm">
                            <option value="">Set priority…</option>
                            {(metadata.priorities || []).map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                        <button onClick={bulkComplete} data-testid="tasks-bulk-complete" className="h-9 px-3 rounded-md bg-[#3E9E2E] text-white text-sm">Complete</button>
                        <button onClick={bulkCancel} data-testid="tasks-bulk-cancel" className="h-9 px-3 rounded-md border border-[#D1D5DB] bg-white text-sm">Cancel</button>
                        {isAdmin && (
                            <button
                                onClick={() => setBulkDeleteOpen(true)}
                                data-testid="bulk-tasks-delete"
                                className="h-9 px-3 rounded-md bg-[#DA3535] hover:bg-[#B92E2E] text-white text-sm font-medium"
                            >
                                Delete
                            </button>
                        )}
                        <button onClick={() => setSelected(new Set())} data-testid="bulk-tasks-clear" className="ml-auto text-sm text-[#4B5565] hover:underline">Clear</button>
                    </div>
                )}

                {error && (
                    <div className="mb-3 p-3 rounded-md border border-[#DA3535]/30 bg-[#FEF3F3] text-sm text-[#991B1B]">
                        {error} · <button className="underline" onClick={load}>Retry</button>
                    </div>
                )}

                {/* Body */}
                {loading ? (
                    <div className="text-sm text-[#4B5565] py-10 text-center" data-testid="tasks-loading">Loading tasks…</div>
                ) : tasks.length === 0 ? (
                    <div className="text-center py-14 border border-dashed border-[#E5E7EB] rounded-xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]" data-testid="tasks-empty">
                        <div className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-[#F1F5F9] text-[#475569] mb-3">
                            <CheckSquare size={20} strokeWidth={1.75} />
                        </div>
                        <div className="text-[#131B2F] text-base font-semibold">No tasks match your filters</div>
                        <div className="text-sm text-[#4B5565] mt-1">Try clearing filters, or create a new task.</div>
                    </div>
                ) : view === "list" ? (
                    <ListView
                        tasks={tasks}
                        engMap={engMap}
                        selected={selected}
                        setSelected={setSelected}
                        canManageProjects={canManageProjects}
                        onEdit={(t) => {
                            if (t.kind === "workflow") {
                                openCanonicalTask(t);
                                return;
                            }
                            setFormInitial(t); setFormOpen(true);
                        }}
                        onOpen={(t) => {
                            // FIELD-001C · Task click opens the SAME shared
                            // TaskForm the New task button opens, populated
                            // with the task's saved values. Permission-aware
                            // gating happens inside the form; there is no
                            // intermediate summary drawer.
                            // VISIT-TASK-001D2 · Canonical (workflow) rows
                            // route to the role-aware project detail
                            // surface so Ops does not fall back to
                            // ``/operations`` via the installer
                            // ProtectedRoute redirect.
                            if (t.kind === "workflow") {
                                openCanonicalTask(t);
                                return;
                            }
                            setFormInitial(t); setFormOpen(true);
                        }}
                    />
                ) : (
                    <KanbanView
                        columns={columns}
                        engMap={engMap}
                        dragOverCol={dragOverCol}
                        setDragOverCol={setDragOverCol}
                        onDropToColumn={onDropToColumn}
                        onOpen={(t) => {
                            // FIELD-001C · Same unified shared TaskForm.
                            // VISIT-TASK-001D2 · Role-aware canonical nav.
                            if (t.kind === "workflow") {
                                openCanonicalTask(t);
                                return;
                            }
                            setFormInitial(t); setFormOpen(true);
                        }}
                    />
                )}

                <div className="mt-3 text-xs text-[#4B5565]" data-testid="tasks-count">
                    {tasks.length} task{tasks.length === 1 ? "" : "s"}
                </div>
            </PageContainer>

            {formOpen && (
                <TaskForm
                    open
                    initial={formInitial}
                    projects={taskProjects.length ? taskProjects : projects}
                    engineers={engineers}
                    metadata={metadata}
                    onClose={async (saved) => {
                        setFormOpen(false);
                        setFormInitial(null);
                        if (saved) await load();
                    }}
                />
            )}

            {/* FIELD-001C · The old summary drawer has been retired.
                Task clicks in List / Kanban open the shared TaskForm
                directly. `detailTask` state is retained as a no-op to
                minimise churn to existing render paths; nothing sets
                it any more. */}

            <BulkDeleteModal
                open={bulkDeleteOpen}
                onCancel={() => setBulkDeleteOpen(false)}
                onDone={handleBulkDone}
                ids={Array.from(selected)}
                labelSingular="task"
                labelPlural="tasks"
                deleter={(batch, ctx) => api.bulkDeleteTasks(batch, ctx)}
                extraWarning="Linked calendar reminders and task-specific notifications will be removed. Audit history for the parent project is retained."
                testidPrefix="bulk-tasks-delete"
            />
        </Shell>
    );
}

/** Category→Appointment type nudge. Not a strict mapping, just a helpful default. */
function mapCategoryToAppointmentType(cat) {
    return {
        Survey: "Site Survey", Installation: "Installation", Electrical: "Electrical Work",
        Commissioning: "Commissioning", Customer: "Handover", Procurement: "Delivery",
        DNO: "Other", Certification: "Other", Administration: "Other", Finance: "Other",
        Warranty: "Maintenance", Planning: "Other",
    }[cat] || "Other";
}

// ─── List view ───────────────────────────────────────────────────────────────

function ListView({ tasks, engMap, selected, setSelected, onEdit, onOpen, canManageProjects = true }) {
    const toggle = (id) => setSelected((s) => {
        const nxt = new Set(s); nxt.has(id) ? nxt.delete(id) : nxt.add(id); return nxt;
    });
    const toggleAll = () => setSelected((s) => s.size === tasks.length ? new Set() : new Set(tasks.map((t) => t.id)));
    return (
        <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-x-auto" data-testid="tasks-list">
            <table className="w-full text-sm">
                <thead className="bg-[#F9FAFB] text-[11px] uppercase tracking-[0.04em] text-[#4B5565] sticky top-0">
                    <tr>
                        {canManageProjects && (
                        <th className="w-10 py-2.5 px-3 text-left">
                            <input
                                type="checkbox"
                                checked={selected.size === tasks.length && tasks.length > 0}
                                onChange={toggleAll}
                                aria-label="Select all"
                                data-testid="bulk-tasks-select-all"
                            />
                        </th>
                        )}
                        <th className="py-2.5 px-3 text-left">Task</th>
                        <th className="py-2.5 px-3 text-left hidden md:table-cell">Customer</th>
                        <th className="py-2.5 px-3 text-left hidden lg:table-cell">Assigned resource</th>
                        <th className="py-2.5 px-3 text-left hidden xl:table-cell">Category</th>
                        <th className="py-2.5 px-3 text-left">Due</th>
                        <th className="py-2.5 px-3 text-left">Priority</th>
                        <th className="py-2.5 px-3 text-left">Status</th>
                        <th className="py-2.5 px-3 text-right">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {tasks.map((t) => {
                        const eng = t.assigned_engineer_id ? engMap[t.assigned_engineer_id] : null;
                        const isWorkflow = t.kind === "workflow";
                        // FIELD-001B1 · Engineer name fallback so unified
                        // canonical rows (which come with a resolved
                        // `assigned_engineer_name` but may not be in the
                        // local engMap when the roster hides archived) still
                        // display an identity, not "Unassigned".
                        const displayEngineerName = eng?.displayName || t.assigned_engineer_name || null;
                        return (
                            <tr key={t.id} className={`border-t border-[#F3F4F6] hover:bg-[#F9FAFB] ${t.is_overdue ? "bg-[#FEF3F3]/40" : ""}`} data-testid={`tasks-row-${t.id}`}>
                                {canManageProjects && (
                                <td className="py-2.5 px-3">
                                    {!isWorkflow ? (
                                        <input
                                            type="checkbox"
                                            checked={selected.has(t.id)}
                                            onChange={() => toggle(t.id)}
                                            aria-label="Select task"
                                            data-testid={`bulk-task-checkbox-${t.id}`}
                                        />
                                    ) : null}
                                </td>
                                )}
                                <td className="py-2.5 px-3">
                                    <button onClick={() => onOpen(t)} className="text-left" data-testid={`tasks-open-${t.id}`}>
                                        <div className="flex items-center gap-1.5">
                                            <span className="font-medium text-[#131B2F]">{t.title}</span>
                                            {isWorkflow && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[#EEF2FF] text-[#3730A3]"
                                                      data-testid={`tasks-kind-workflow-${t.id}`}>
                                                    Workflow
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-[#4B5565] mt-0.5 line-clamp-1">
                                            {t.description || t.notes || t.project_address}
                                        </div>
                                    </button>
                                </td>
                                <td className="py-2.5 px-3 hidden md:table-cell text-[#131B2F]">{t.project_customer_name}</td>
                                <td className="py-2.5 px-3 hidden lg:table-cell">
                                    {displayEngineerName ? (
                                        <span className="inline-flex items-center gap-1.5 text-[#131B2F]" data-testid={`tasks-engineer-${t.id}`}>
                                            {eng?.colour && (
                                                <span className="w-2.5 h-2.5 rounded-full" style={{ background: eng.colour }} />
                                            )}
                                            {displayEngineerName}
                                        </span>
                                    ) : <span className="text-[#4B5565] italic" data-testid={`tasks-engineer-${t.id}`}>Unassigned</span>}
                                </td>
                                <td className="py-2.5 px-3 hidden xl:table-cell text-[#4B5565]">{t.task_category}</td>
                                <td className="py-2.5 px-3">
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-[#131B2F]">{formatDue(t) || "—"}</span>
                                        {t.is_overdue && <OverdueBadge small />}
                                    </div>
                                </td>
                                <td className="py-2.5 px-3"><PriorityBadge priority={t.priority} small /></td>
                                <td className="py-2.5 px-3"><StatusBadge status={t.status} small /></td>
                                <td className="py-2.5 px-3 text-right">
                                    {canManageProjects && !isWorkflow ? (
                                        <button onClick={() => onEdit(t)} className="text-[#3E9E2E] hover:underline text-sm mr-2" data-testid={`tasks-edit-${t.id}`}>
                                            Edit
                                        </button>
                                    ) : isWorkflow ? (
                                        <button onClick={() => onOpen(t)} className="text-[#3E9E2E] hover:underline text-sm mr-2" data-testid={`tasks-open-project-${t.id}`}>
                                            Open project
                                        </button>
                                    ) : null}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// ─── Kanban view ─────────────────────────────────────────────────────────────

function KanbanView({ columns, engMap, dragOverCol, setDragOverCol, onDropToColumn, onOpen }) {
    // DEV-021 · Column tint by state — restrained, borderless
    // background differentiation so operators can scan columns
    // without a coloured stripe on every card.
    const COLUMN_TINT = {
        "Not Started": "bg-[#F1F5F9]",
        "In Progress": "bg-[#EFF6FF]",
        "Waiting":     "bg-[#FEF3C7]/60",
        "Completed":   "bg-[#ECFDF5]",
    };
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="tasks-kanban">
            {KANBAN_COLUMNS.map((col) => {
                const items = columns[col] || [];
                const tint = COLUMN_TINT[col] || "bg-[#F9FAFB]";
                return (
                    <div
                        key={col}
                        onDragOver={(e) => { e.preventDefault(); setDragOverCol(col); }}
                        onDragLeave={() => setDragOverCol(null)}
                        onDrop={(e) => {
                            e.preventDefault();
                            const id = e.dataTransfer.getData("text/task-id");
                            if (id) onDropToColumn(col, id);
                        }}
                        data-testid={`kanban-col-${col.replace(/\s/g, "-").toLowerCase()}`}
                        className={`rounded-xl border p-3 min-h-[420px] ${tint} ${dragOverCol === col ? "border-[#3E9E2E] ring-2 ring-[#3E9E2E]/20" : "border-[#E5E7EB]"} transition-colors`}
                    >
                        <div className="flex items-center gap-2 mb-3 px-1">
                            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#4B5565]">
                                {col}
                            </div>
                            <span className="inline-flex items-center justify-center min-w-[22px] h-[20px] px-1.5 rounded-full bg-white text-[11px] font-semibold tabular-nums text-[#131B2F] border border-[#E5E7EB]">
                                {items.length}
                            </span>
                        </div>
                        <div className="space-y-2">
                            {items.map((t) => {
                                const eng = t.assigned_engineer_id ? engMap[t.assigned_engineer_id] : null;
                                return (
                                    <div
                                        key={t.id}
                                        draggable
                                        onDragStart={(e) => e.dataTransfer.setData("text/task-id", t.id)}
                                        onClick={() => onOpen(t)}
                                        data-testid={`kanban-card-${t.id}`}
                                        className="bg-white rounded-lg border border-[#E5E7EB] p-3 cursor-grab active:cursor-grabbing shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-md hover:border-[#CBD5E1] transition-all"
                                    >
                                        <div className="text-sm font-semibold text-[#131B2F] line-clamp-2">{t.title}</div>
                                        <div className="text-xs text-[#4B5565] mt-0.5 line-clamp-1">{t.project_customer_name}</div>
                                        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                                            <PriorityBadge priority={t.priority} small />
                                            {t.is_overdue && <OverdueBadge small />}
                                        </div>
                                        <div className="mt-2 flex items-center justify-between text-xs text-[#4B5565]">
                                            <span>{formatDue(t) || "—"}</span>
                                            {(() => {
                                                // FIELD-001C · Kanban must show the assignee name even
                                                // when the local `engMap` misses the row (Leads can't
                                                // read the Ops-only roster). Fall back to the task's
                                                // own resolved `assigned_engineer_name`.
                                                const displayName = eng?.displayName || t.assigned_engineer_name || null;
                                                return displayName ? (
                                                    <span className="inline-flex items-center gap-1 truncate max-w-[120px]">
                                                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: eng?.colour || "#3E9E2E" }} />
                                                        <span className="truncate">{displayName}</span>
                                                    </span>
                                                ) : <span className="italic">Unassigned</span>;
                                            })()}
                                        </div>
                                    </div>
                                );
                            })}
                            {items.length === 0 && (
                                <div className="text-xs text-[#4B5565] text-center py-8 border border-dashed border-[#E5E7EB] rounded-lg bg-white/60">
                                    No tasks
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ─── Detail drawer ───────────────────────────────────────────────────────────

function TaskDetail({ task, engineers, onClose, onEdit, onRefresh, onOpenProject, onCreateAppointment, onOpenSameTask }) {
    const { user } = useAuth();
    const canManageProjects = Array.isArray(user?.permissions)
        && user.permissions.includes("manage_projects");
    const [reason, setReason] = useState("");
    const [showCancel, setShowCancel] = useState(false);
    if (!task) return null;
    const engMap = Object.fromEntries((engineers || []).map((e) => [e.id, e]));
    const eng = task.assigned_engineer_id ? engMap[task.assigned_engineer_id] : null;
    // FIELD-001C · Action-specific gating. Field users may only
    // change status on tasks personally assigned to them. Cancel
    // stays Ops-only (server enforces MANAGE_PROJECTS on
    // `POST /tasks/{id}/cancel`). Reopen is not exposed here.
    const isAssigneeOfMe = (task.assigned_engineer_id === user?.engineer_id)
        || (Array.isArray(task.assigned_engineer_ids)
            && !!user?.engineer_id
            && task.assigned_engineer_ids.includes(user.engineer_id));
    const canStatusUpdate = canManageProjects || isAssigneeOfMe;
    const canCancel = canManageProjects;
    const canEditForm = canManageProjects;

    async function complete() {
        try {
            const fresh = await api.completeTask(task.id);
            toast.success("Task completed");
            onRefresh?.();
            // FIELD-001C · Refresh the drawer state so the identity
            // and available actions update in-place (the parent list
            // reload alone doesn't reach the open drawer).
            if (fresh && onOpenSameTask) onOpenSameTask(fresh); else onClose?.();
        }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
    }
    async function cancel() {
        try { await api.cancelTask(task.id, reason); toast.success("Task cancelled"); onRefresh?.(); onClose?.(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
    }

    return (
        <div className="fixed inset-0 z-50" data-testid="task-detail-drawer">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <aside className="absolute right-0 top-0 bottom-0 w-full sm:max-w-md bg-white shadow-2xl flex flex-col">
                <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <PriorityBadge priority={task.priority} />
                            <StatusBadge status={task.status} />
                            {task.is_overdue && <OverdueBadge />}
                        </div>
                        <h3 className="text-lg font-semibold text-[#131B2F]">{task.title}</h3>
                        <div className="text-sm text-[#4B5565] mt-0.5">{task.task_category}</div>
                    </div>
                    <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#F5F5F7] text-[#4B5565]" aria-label="Close" data-testid="task-detail-close">
                        <X size={18} strokeWidth={1.75} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                    <div>
                        <div className="text-xs uppercase tracking-[0.08em] text-[#4B5565] mb-1">Project</div>
                        <div className="text-sm font-medium text-[#131B2F]">{task.project_customer_name || "—"}</div>
                        {task.project_address && <div className="text-xs text-[#4B5565] mt-0.5">{task.project_address}</div>}
                        <button onClick={onOpenProject} data-testid="task-detail-open-project" className="mt-2 inline-flex items-center gap-1.5 text-sm text-[#3E9E2E] hover:underline">
                            <ExternalLink size={14} strokeWidth={1.75} /> Open project
                        </button>
                    </div>

                    <div>
                        <div className="text-xs uppercase tracking-[0.08em] text-[#4B5565] mb-1">Assigned resource</div>
                        {(() => {
                            // FIELD-001C · Drawer must show the assignee even when the
                            // local `engMap` misses the row (Leads can't read the
                            // Ops-only roster). Fall back to the task's resolved
                            // `assigned_engineer_name`.
                            const displayName = eng?.displayName || task.assigned_engineer_name || null;
                            if (displayName) {
                                return (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white" style={{ background: eng?.colour || "#3E9E2E" }}>
                                        {displayName}{eng?.active === false && " (archived)"}
                                    </span>
                                );
                            }
                            return <span className="text-sm italic text-[#4B5565]">Unassigned</span>;
                        })()}
                    </div>

                    {(task.due_date || task.due_time) && (
                        <div>
                            <div className="text-xs uppercase tracking-[0.08em] text-[#4B5565] mb-1">Due</div>
                            <div className="text-sm text-[#131B2F]">{formatDue(task)}</div>
                        </div>
                    )}

                    {task.description && (
                        <div>
                            <div className="text-xs uppercase tracking-[0.08em] text-[#4B5565] mb-1">Description</div>
                            <div className="text-sm text-[#131B2F] whitespace-pre-wrap">{task.description}</div>
                        </div>
                    )}
                    {task.notes && (
                        <div>
                            <div className="text-xs uppercase tracking-[0.08em] text-[#4B5565] mb-1">Notes</div>
                            <div className="text-sm text-[#131B2F] whitespace-pre-wrap">{task.notes}</div>
                        </div>
                    )}

                    {task.due_date && task.due_time && task.status !== "Completed" && task.status !== "Cancelled" && (
                        <button
                            onClick={onCreateAppointment}
                            data-testid="task-detail-create-appointment"
                            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-[#3E9E2E]/40 bg-[#3E9E2E]/10 text-[#166534] text-sm font-medium hover:bg-[#3E9E2E]/16"
                        >
                            <CalendarIcon size={14} strokeWidth={1.75} /> Create calendar appointment
                        </button>
                    )}

                    {showCancel && (
                        <div className="border border-[#DA3535]/30 rounded-lg p-3 bg-[#FEF3F3]">
                            <div className="text-sm font-medium text-[#131B2F] mb-2">Cancel this task?</div>
                            <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" rows={2} data-testid="task-cancel-reason" className="w-full px-2.5 py-2 border border-[#D1D5DB] rounded-md text-sm bg-white" />
                            <div className="mt-2 flex gap-2">
                                <button onClick={() => setShowCancel(false)} className="px-3 py-1.5 text-sm rounded-md border border-[#D1D5DB]">Keep it</button>
                                <button onClick={cancel} data-testid="task-cancel-confirm" className="px-3 py-1.5 text-sm rounded-md bg-[#DA3535] text-white">Confirm cancel</button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="px-5 py-4 border-t border-[#E5E7EB] flex flex-wrap gap-2 justify-end">
                    {canEditForm && (
                    <button onClick={onEdit} data-testid="task-detail-edit" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#D1D5DB] text-sm font-medium hover:bg-[#F5F5F7]">
                        <Pencil size={14} strokeWidth={1.75} /> Edit
                    </button>
                    )}
                    {task.status !== "Completed" && task.status !== "Cancelled" && canStatusUpdate && (
                        <>
                            <button onClick={complete} data-testid="task-detail-complete" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#3E9E2E]/50 text-[#166534] bg-[#DCFCE7] text-sm font-medium hover:bg-[#BBF7D0]">
                                <CheckCircle2 size={14} strokeWidth={1.75} /> Complete
                            </button>
                            {canCancel && (
                            <button onClick={() => setShowCancel(true)} data-testid="task-detail-cancel-open" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#DA3535]/40 text-[#991B1B] bg-[#FEE2E2] text-sm font-medium hover:bg-[#FECACA]">
                                <XCircle size={14} strokeWidth={1.75} /> Cancel
                            </button>
                            )}
                        </>
                    )}
                </div>
            </aside>
        </div>
    );
}

// (Detail drawer uses the React `useState` imported at the top of the file.)
